import { useNavigate } from 'react-router-dom'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import type { ApplicationError, Return } from 'modules/core/lib/asyncAction'
import type { Adapter } from 'modules/core/lib/mobx-rest/types'

import { useEmployeesImporter } from 'modules/employees/lib/employeesImporterContext'

interface Props {
  api: Adapter
  file: File
  importPath: string
}
interface ReturnType {
  running: boolean
  onSubmit: (closePortal: ClosePortalFn) => Promise<Return<void>>
  error: boolean
  value: void | ApplicationError | null
}

const useProcessFile = ({ api, file, importPath }: Props): ReturnType => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const importer = useEmployeesImporter()

  const {
    running,
    performAction: onSubmit,
    error,
    value,
  } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (!file) return

      const payload = await api.post(importPath, { file })
      const { token, employees, attributes, custom_fields: customFields } = payload

      importer.setEmployees({
        employees,
        attributes,
        customFields,
        isOverlimited: payload.is_overlimited,
        newEmployees: payload.new_employees,
        currentSeats: payload.current_seats,
        maxEmployeesAllowedImport: payload.max_employees_allowed_import,
        templateId: payload.template_id,
      })

      importer.setToken(token)
      closePortal({
        preventOnCloseCallbackAfterClose: true,
      })
      navigate(navigation.employees.import.fullPath())
    },
    {
      errorFlash: false,
    }
  )

  return {
    running,
    onSubmit,
    error,
    value,
  }
}

export default useProcessFile
